import React, { useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { FiMinus } from "react-icons/fi";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FormHelperText, formStyles } from "./FormUtils";

const Dropdown = ({
  options = [],
  placeholder = "Select an option",
  defaultOption = null,
  displayKey = "name",
  idKey = "_id",
  isSearchable = false,
  disabled = false,
  wrapperClassName = "",
  buttonClassName = "",
  dropdownClassName = "",
  className = "",
  optionClassName = "",
  filterClassName = "",
  outerContainerStyle = "",
  selectedValueClassName = "",
  selectedButtonClassName = "",
  placeholderClassName = "",
  truncateDisplay = false,
  maxDisplayLength = 20,
  onSelect,
  error,
  label,
  type,
  labelStyle,
  isRequired = false,
  name,
  onBlur,
  onFocus,
  allowFillColor = false,
  allowClear = false,
  dropdownTyping = false,
  errorMessage,
  errorContainerStyle = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        if (isOpen) {
          setIsOpen(false);
          onBlur && onBlur();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (
      defaultOption &&
      (!selectedOption || selectedOption[idKey] !== defaultOption[idKey])
    ) {
      setSelectedOption(defaultOption);

      if (!isOpen) {
        setSearchTerm(getDisplayValue(defaultOption));
      }
      onSelect && onSelect(defaultOption);
    }
  }, [defaultOption, isOpen]);

  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (!defaultOption) {
      setSelectedOption(null);
      setSearchTerm("");
      // Optional: notify parent that it's cleared
      onSelect && onSelect(null);
    }
    // NOTE: we intentionally do NOT depend on selectedOption here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption]);

  const dataOptions =
    type === "year"
      ? Array.from({ length: 30 }, (_, i) => {
          const year = (new Date().getFullYear() - i).toString();
          return { _id: year, name: year };
        })
      : options;

  const getDisplayValue = (option) => {
    if (!option) return "";
    if (displayKey.includes("+")) {
      return displayKey
        .split("+")
        .map((key) => option[key.trim()])
        .join(" ");
    }
    return option[displayKey] || "";
  };

  const isTypedValueExist = dataOptions.some(
    (opt) =>
      getDisplayValue(opt).toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const filteredOptions = dataOptions.filter((option) =>
    getDisplayValue(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm("");
    setIsOpen(false);
    setFocusedIndex(-1);
    onSelect && onSelect(option);
    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef.current.blur(); // force blur
        buttonRef.current.focus(); // immediately restore focus
      }
    }, 0);
    if (name && onBlur) onBlur(name, getDisplayValue(option));
  };

  const handleAddTypedOption = () => {
    const custom = { [idKey]: null, [displayKey]: searchTerm, isCustom: true };
    handleSelect(custom);
  };

  const handleKeyDown = (e) => {
    const key = e.key;

    // Open dropdown on Enter or Space if closed
    if (!isOpen && (key === "Enter" || key === " ")) {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);
      return;
    }

    if (!isOpen) return; // Do nothing if closed and it's not a toggle key

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;

      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex]);
        } else if (searchTerm.trim() && dropdownTyping && !isTypedValueExist) {
          handleAddTypedOption();
        }
        break;

      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setTimeout(() => {
          if (buttonRef.current) buttonRef.current.focus();
        }, 0);
        break;

      default:
        break;
    }
  };

  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm("");
    onSelect && onSelect(null);
  };

  return (
    <div className={twMerge("flex flex-col  w-full ", outerContainerStyle)}>
      {label && (
        <div className="flex items-center gap-2">
          <label
            className={twMerge(
              "text-light-gray font-inter font-medium text-sm ",
              formStyles.dropDownlabel,
              labelStyle
            )}
          >
            {label} {isRequired && <span className="text-light-red">*</span>}
          </label>
          {errorMessage && (
            <span className="text-[#C00707] text-[12px] leading-[22px] font-['Public_Sans'] font-normal ml-2 whitespace-nowrap mb-2">
              {errorMessage}
            </span>
          )}
        </div>
      )}

      <div ref={dropdownRef} className={twMerge("relative", wrapperClassName)}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => {
            setIsOpen((prev) => !prev);
            setSearchTerm("");
            setFocusedIndex(0);
            onFocus && onFocus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !e.shiftKey && isOpen && isSearchable) {
              e.preventDefault();
              setTimeout(() => searchInputRef.current?.focus(), 0);
            } else {
              handleKeyDown(e);
            }
          }}
          onBlur={() => {
            // If focus moved outside dropdown entirely, run blur validation
            setTimeout(() => {
              const active = document.activeElement;
              if (
                dropdownRef.current &&
                !dropdownRef.current.contains(active)
              ) {
                onBlur && onBlur();
              }
            }, 0);
          }}
          className={twMerge(
            formStyles.dropdownButton,
            error || errorMessage
              ? "border-[#FF4D4F] dark:border-red-500 focus:border-[#FF4D4F]"
              : "",
            buttonClassName
          )}
        >
          <span
            className={twMerge(
              "truncate text-left w-full ",
              selectedOption
                ? "opacity-100 font-medium"
                : "text-[#12121257]/90 dark:text-white/60",
              selectedValueClassName,
              placeholderClassName
            )}
          >
            {selectedOption
              ? getDisplayValue(selectedOption)
              : placeholder || "Select an option"}
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-[#808080]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#808080]" />
          )}
        </button>

        {isOpen && (
          <div
            className={twMerge(formStyles.dropdownMenu, dropdownClassName)}
            role="listbox"
            tabIndex={-1}
          >
            {isSearchable && (
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                className="w-full px-2 py-2 text-[#808080] rounded bg-[#454444]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setIsOpen(false);
                    setTimeout(() => buttonRef.current?.focus(), 0);
                  } else if (e.key === "Tab" && !e.shiftKey) {
                    e.preventDefault();
                    setTimeout(() => buttonRef.current?.focus(), 0);
                  } else {
                    handleKeyDown(e);
                  }
                }}
              />
            )}

            <div
              tabIndex={0}
              className={twMerge(
                "max-h-[190px] overflow-y-auto scrollbar-no-radius",
                filterClassName
              )}
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={option[idKey]}
                  ref={(el) => (optionRefs.current[index] = el)}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={
                    selectedOption && option[idKey] === selectedOption[idKey]
                  }
                  className={twMerge(
                    formStyles.dropdownOption,
                    optionClassName
                  )}
                >
                  <span className="truncate text-foreground">
                    {getDisplayValue(option)}
                  </span>
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="px-4 py-2 text-gray-500 font-inter font-medium text-center">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}

        {selectedOption && !disabled && allowClear !== false && (
          <div
            onClick={handleClear}
            className="absolute top-[10px] right-[36px] w-4 h-4 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 cursor-pointer"
          >
            <FiMinus className="w-3 h-3 text-red-500" />
          </div>
        )}

        <FormHelperText
          error={errorMessage || error}
          className={errorContainerStyle}
          errorClassName={formStyles.errorText}
        />
      </div>
    </div>
  );
};

export default Dropdown;
